import { useParams, Link, Navigate } from "react-router-dom";
import { useRef, useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Tabs } from "@mantine/core";
import { gql } from "@/__generated__";
import { getMedicalInfoTabsForCondition } from "shared/src/data/medical-info-tabs";
import Navbar from "@/components/layout/Navbar/Navbar";
import { ReferPatientProgressTracker } from "@/components/ReferPatientProgressTracker";
import { StrengthenReferral } from "../StrengthenReferral/StrengthenReferral";
import { PageFooter } from "@/components/layout/PageFooter";
import {
  FilteredSurveyTab,
  FilteredSurveyTabHandle,
} from "../StrengthenReferral/components/tabs/FilteredSurveyTab/FilteredSurveyTab";
import styles from "./medical-info.module.scss";
import { flushSync } from "react-dom";
import { useFormContext } from "@/contexts/FormContext";

const GET_APPEAL_FOR_MEDICAL_INFO = gql(`
  query GetAppealForMedicalInfo($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      status {
        referred
      }
      denial {
        condition
      }
    }
  }
`);

interface MedicalInfoTabProps {
  tabId: string;
}

export function MedicalInfoTab({ tabId }: MedicalInfoTabProps) {
  const { appealId } = useParams<{ appealId: string }>();
  const surveyRef = useRef<FilteredSurveyTabHandle>(null);
  const { resetDirty } = useFormContext();
  const [isSaving, setIsSaving] = useState(false);

  const { data, loading: statusLoading } = useQuery(
    GET_APPEAL_FOR_MEDICAL_INFO,
    {
      variables: { appealId: appealId! },
      skip: !appealId,
      fetchPolicy: "no-cache",
    },
  );

  const condition = data?.getAppealById?.denial?.condition;
  const readOnly = data?.getAppealById?.status?.referred ?? false;

  const tabs = useMemo(
    () => getMedicalInfoTabsForCondition(condition),
    [condition],
  );
  const currentTab = tabs.find((t) => t.id === tabId);

  if (!appealId) {
    return null;
  }

  if (!statusLoading && !currentTab && tabs.length > 0) {
    return (
      <Navigate
        to={`/appeals/${appealId}/strengthen/medical-info/${tabs[0].route}`}
        replace
      />
    );
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const success = await surveyRef.current?.save();
      if (success) {
        flushSync(() => {
          resetDirty();
        });
      }
      return success ?? false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBeforeSend = async () => {
    return await handleSave();
  };

  const footer =
    !statusLoading && !readOnly ? (
      <PageFooter
        primaryButton={{
          text: "Save",
          onClick: handleSave,
          loading: isSaving,
          disabled: isSaving,
        }}
      />
    ) : undefined;

  const tabContent = (
    <div className={styles.container}>
      <div className={styles.sideTabsContainer}>
        <Tabs value={tabId} orientation="vertical" className={styles.sideTabs}>
          <Tabs.List>
            {tabs.map((tab) => (
              <Tabs.Tab key={tab.id} value={tab.id}>
                <Link
                  to={`/appeals/${appealId}/strengthen/medical-info/${tab.route}`}
                >
                  {tab.label}
                </Link>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </div>
      <div className={styles.contentContainer}>
        {currentTab && (
          <FilteredSurveyTab
            ref={surveyRef}
            appealId={appealId}
            readOnly={readOnly}
            filterTag={currentTab.surveyTag}
          />
        )}
      </div>
    </div>
  );

  return (
    <Navbar
      titleComponent={
        <ReferPatientProgressTracker
          appealId={appealId}
          onBeforeSendReferral={handleSaveBeforeSend}
        />
      }
      footer={footer}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="medical-info"
        tabContent={tabContent}
      />
    </Navbar>
  );
}
